import { Injectable, Injector } from '@angular/core';
import { DocumentChangeAction } from '@angular/fire/compat/firestore';
import { collection, Firestore, getDocs, limit, orderBy, query, where } from '@angular/fire/firestore';
import { AuthService } from '@core/auth.service';
import { AuthStateService } from '@core/auth-state.service';
import { CatchErrorService } from '@core/catch-error.service';
import { BehaviorSubject, catchError, from, of, shareReplay, switchMap, take } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthSyndicateService {

  latestOngoingSyndicate$ = new BehaviorSubject(null);

  snapShotData(actions: DocumentChangeAction<unknown>[]): any {
    throw new Error('Method not implemented.');
  }

  constructor(private catchService: CatchErrorService,
    private firestore: Firestore,
    private injector: Injector,
    private authStateService: AuthStateService
  ) {
    this.fetchLatestOngoingSyndicate();

  }

  // This code gets the user's request to join a syndicate and stores it in a
  // BehaviorSubject. The BehaviorSubject is then subscribed to by the
  // syndicate-join component, which displays the user's request to join the
  // syndicate.
  private userSyndicateRequest$ = new BehaviorSubject(5);
  // Updates the page size for the user syndicate requests
  updateSyndicateRequest(pageSize: number) {
    this.userSyndicateRequest$.next(pageSize);
  }

  userSyndicate$ = this.userSyndicateRequest$.pipe(
    switchMap((pageSize: any) => {
      // Gate on customTokenReady$ to prevent permission-denied errors on iOS/Safari.
      return this.authStateService.customTokenReady$.pipe(
        take(1),
        switchMap(() => {
          const authService = this.injector.get(AuthService);
          return authService.getUid$("SWServiceService").pipe(
            switchMap((userId) => {
              return userId && userId.length > 5
                ? this.catchService.isOnline$.pipe(
                  switchMap((isOnline) => {
                    if (isOnline) {
                      const userSyndicateRef = collection(
                        this.firestore,
                        `/users/${userId}/userSyndicate_list`
                      );
                      const userSyndicateQuery = query(
                        userSyndicateRef,
                        where("hide", "==", false),
                        orderBy("creation_date", "desc"),
                        limit(pageSize)
                      );
                      return getDocs(userSyndicateQuery).then((snapshot) =>
                        snapshot.docs.map((doc) => doc.data())
                      );
                    }
                    return of(null);
                  }),
                  shareReplay(1)
                )
                : of(null);
            }));
        })
      );
    })
  );


  // This function is used to get latest ongoing syndicate to take payment holiday
  async getLatestOngoingSyndicate(): Promise<any> {
    try {
      const authService = this.injector.get(AuthService);
      // Gate on customTokenReady$ to prevent permission-denied errors on iOS/Safari.
      return this.authStateService.customTokenReady$.pipe(
        take(1),
        switchMap(() => authService.getUid$("SWServiceService")),
        switchMap(async (userId) => {
          if (!userId) return null;
          const managedTicketsRef = collection(this.firestore, `/users/${userId}/managed_tickets`);
          const ongoingSyndicateQuery = query(
            managedTicketsRef,
            where("syndicate_type", "==", "Ongoing"),
            orderBy("creation_date", "desc"),
            limit(1)
          );

          const querySnapshot = await getDocs(ongoingSyndicateQuery);
          if (!querySnapshot.empty) {
            const latestSyndicate = querySnapshot.docs[0].data();
            return latestSyndicate;
          } else {
            return null; // No ongoing syndicate found
          }
        }),
        catchError((error) => {
          console.error("Error fetching ongoing syndicate:", error);
          return of(null);
        })
      );
    } catch (error) {
      console.error("Error fetching ongoing syndicate:", error);
      return null; // Error occurred
    }
  }

  // This function is used to send latestSyndicate data to observable
  fetchLatestOngoingSyndicate() {
    try {
      // getLatestOngoingSyndicate now returns an Observable; subscribe to it.
      this.getLatestOngoingSyndicate().then((obs: any) => {
        if (obs && typeof obs.subscribe === 'function') {
          obs.subscribe((latestSyndicate: any) => {
            this.latestOngoingSyndicate$.next(latestSyndicate);
          });
        } else {
          this.latestOngoingSyndicate$.next(obs);
        }
      }).catch((error: any) => {
        console.error("Error fetching ongoing syndicate:", error);
      });
    } catch (error) {
      return error;
    }
  }

  // This function converts a duration in milliseconds to a formatted string representing the time.
  convertMillisecondsToTime(milliseconds: number): string {
    if (milliseconds < 0) {
      return ` `;
    }
    let seconds = milliseconds / 1000;
    if (seconds < 60) {
      return `${seconds.toFixed(2)}s`;
    } else {
      let minutes = Math.floor(seconds / 60);
      seconds = seconds % 60;
      return `${minutes}m ${seconds.toFixed(2)}s`;
    }
  }

  // update reverification success in sms collection
    updateSmsDeliveryReceipt(docId: string) {
      const path_id = "xUQEwEfWJs93sMIGTZDM";
      const data = { reverified: true };
      const dv = { docId: docId };
      const method = "update";

    const authService = this.injector.get(AuthService);
    return from(
      authService.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data: data,
        dv,
        method
      })
    ).subscribe({
      error: (error) => console.error("Error updating document:", error)
    });
  }

}
